<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import schedulingAPI from '../api/scheduling';
import SchedulingDropdown from './SchedulingDropdown.vue';

const props = defineProps({
  services: {
    type: Array,
    default: () => [],
  },
  professionals: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['open-appointment-modal', 'open-prontuario-modal']);

const appointments = ref([]);
const isLoading = ref(false);
const selectedProfessionalId = ref('');
const selectedServiceId = ref('');

const filteredProfessionals = computed(() => {
  if (!selectedServiceId.value) return props.professionals;
  const sId = Number(selectedServiceId.value);
  return props.professionals.filter(p => {
    if (!p.service_ids || p.service_ids.length === 0) return true;
    return p.service_ids.map(Number).includes(sId);
  });
});

const filteredServices = computed(() => {
  if (!selectedProfessionalId.value) return props.services;
  const prof = props.professionals.find(p => Number(p.id) === Number(selectedProfessionalId.value));
  if (!prof || !prof.service_ids || prof.service_ids.length === 0) return props.services;
  const profServiceIds = prof.service_ids.map(Number);
  return props.services.filter(s => profServiceIds.includes(Number(s.id)));
});

const professionalOptions = computed(() => [
  { value: '', label: 'Todos os Profissionais' },
  ...filteredProfessionals.value.map(p => ({
    value: p.id,
    label: `${p.name} (${p.specialty || 'Geral'})`,
  })),
]);

const serviceOptions = computed(() => [
  { value: '', label: 'Todos os Serviços' },
  ...filteredServices.value.map(s => ({
    value: s.id,
    label: s.name,
    color: s.color,
  })),
]);

const onProfessionalChange = () => {
  if (selectedServiceId.value) {
    const validServiceIds = filteredServices.value.map(s => String(s.id));
    if (!validServiceIds.includes(String(selectedServiceId.value))) {
      selectedServiceId.value = '';
    }
  }
};

const onServiceChange = () => {
  if (selectedProfessionalId.value) {
    const validProfIds = filteredProfessionals.value.map(p => String(p.id));
    if (!validProfIds.includes(String(selectedProfessionalId.value))) {
      selectedProfessionalId.value = '';
    }
  }
};

// Shift / Period Filter: 'all' | 'morning' | 'afternoon' | 'night'
const selectedPeriod = ref('all');

const periods = [
  { id: 'all', label: 'Dia Todo', range: '06h - 23h' },
  { id: 'morning', label: 'Manhã', range: '06h - 12h' },
  { id: 'afternoon', label: 'Tarde', range: '12h - 18h' },
  { id: 'night', label: 'Noite', range: '18h - 23h' },
];

const morningSlots = ['06:00', '07:00', '08:00', '09:00', '10:00', '11:00'];
const afternoonSlots = ['12:00', '13:00', '14:00', '15:00', '16:00', '17:00'];
const nightSlots = ['18:00', '19:00', '20:00', '21:00', '22:00'];

const timeSlots = computed(() => {
  if (selectedPeriod.value === 'morning') return morningSlots;
  if (selectedPeriod.value === 'afternoon') return afternoonSlots;
  if (selectedPeriod.value === 'night') return nightSlots;
  return [...morningSlots, ...afternoonSlots, ...nightSlots];
});

const getPeriodCount = periodId => {
  if (periodId === 'all') return appointments.value.length;
  return appointments.value.filter(app => {
    const { hour } = parseAppDateTime(app.start_time);
    if (hour < 0) return false;
    if (periodId === 'morning') return hour >= 6 && hour < 12;
    if (periodId === 'afternoon') return hour >= 12 && hour < 18;
    if (periodId === 'night') return hour >= 18 && hour <= 23;
    return true;
  }).length;
};

// Current week state
function getMonday(d) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(date.setDate(diff));
}

function getLocalDateString(d) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const currentWeekStart = ref(getMonday(new Date()));

const weekDays = computed(() => {
  const days = [];
  const start = new Date(currentWeekStart.value);
  const todayStr = getLocalDateString(new Date());

  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const dateStr = getLocalDateString(d);
    const rawDayName = new Intl.DateTimeFormat('pt-BR', { weekday: 'short' }).format(d).toUpperCase().replace('.', '');
    days.push({
      date: d,
      dateString: dateStr,
      dayName: rawDayName,
      formattedDate: new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' }).format(d),
      isToday: dateStr === todayStr,
    });
  }
  return days;
});

const fetchAppointments = async () => {
  isLoading.value = true;
  try {
    const startStr = weekDays.value[0].dateString;
    const endStr = weekDays.value[6].dateString;

    const params = {
      start_date: startStr,
      end_date: endStr,
    };
    if (selectedProfessionalId.value) params.professional_id = selectedProfessionalId.value;
    if (selectedServiceId.value) params.service_id = selectedServiceId.value;

    const res = await schedulingAPI.getAppointments(params);
    appointments.value = res.data || [];
  } catch (err) {
    console.error('Erro ao carregar agendamentos:', err);
  } finally {
    isLoading.value = false;
  }
};

const prevWeek = () => {
  const d = new Date(currentWeekStart.value);
  d.setDate(d.getDate() - 7);
  currentWeekStart.value = d;
};

const nextWeek = () => {
  const d = new Date(currentWeekStart.value);
  d.setDate(d.getDate() + 7);
  currentWeekStart.value = d;
};

const goToToday = () => {
  currentWeekStart.value = getMonday(new Date());
};

const parseAppDateTime = startStr => {
  if (!startStr) return { dateStr: '', hour: -1, timeStr: '00:00' };

  if (typeof startStr === 'string' && startStr.includes('T')) {
    const [datePart, timeWithZone] = startStr.split('T');
    const timeOnly = timeWithZone.split('.')[0].replace('Z', '');
    const [hStr, mStr] = timeOnly.split(':');
    const hour = parseInt(hStr, 10);
    const minute = parseInt(mStr || '0', 10);
    return {
      dateStr: datePart,
      hour,
      timeStr: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
    };
  }

  const clean = String(startStr).replace('T', ' ');
  const parts = clean.split(' ');
  const dateStr = parts[0];
  const timeParts = (parts[1] || '00:00').split(':');
  const hour = parseInt(timeParts[0], 10);
  const minute = parseInt(timeParts[1] || '0', 10);
  return {
    dateStr,
    hour,
    timeStr: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
  };
};

const getAppointmentsForSlot = (dateString, timeLabel) => {
  const targetHour = parseInt(timeLabel.split(':')[0], 10);

  return appointments.value.filter(app => {
    const { dateStr, hour } = parseAppDateTime(app.start_time);
    return dateStr === dateString && hour === targetHour;
  });
};

const getMaxCapacity = (slotAppointments = []) => {
  if (selectedProfessionalId.value) {
    const prof = props.professionals.find(p => Number(p.id) === Number(selectedProfessionalId.value));
    return prof?.max_concurrent_per_slot || 1;
  }

  if (slotAppointments.length > 0) {
    const profIds = [...new Set(slotAppointments.map(a => a.professional?.id).filter(Boolean))];
    const profs = props.professionals.filter(p => profIds.includes(Number(p.id)));
    if (profs.length > 0) {
      return profs.reduce((sum, p) => sum + (p.max_concurrent_per_slot || 1), 0);
    }
  }

  const allCaps = props.professionals.map(p => Number(p.max_concurrent_per_slot) || 1);
  return allCaps.length > 0 ? Math.max(...allCaps) : 1;
};

const isSlotFull = (dateString, timeLabel) => {
  const slotApps = getAppointmentsForSlot(dateString, timeLabel);
  const max = getMaxCapacity(slotApps);
  return slotApps.length >= max;
};

const handleSlotClick = (dateString, timeLabel) => {
  if (isSlotFull(dateString, timeLabel)) return;

  const [hours, minutes] = timeLabel.split(':').map(Number);
  const [year, month, day] = dateString.split('-').map(Number);
  const d = new Date(year, month - 1, day, hours, minutes, 0);

  emit('open-appointment-modal', {
    start_time: d.toISOString(),
    professional_id: selectedProfessionalId.value || null,
    service_id: selectedServiceId.value || null,
  });
};

const handleDayHeaderClick = d => {
  const firstSlot = timeSlots.value[0] || '08:00';
  handleSlotClick(d.dateString, firstSlot);
};

const getStatusBadge = status => {
  switch (status) {
    case 'confirmed':
      return { label: 'Confirmado', bg: 'bg-blue-500/20 text-blue-300 border-blue-500/40' };
    case 'completed':
      return { label: 'Concluído', bg: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' };
    case 'cancelled':
      return { label: 'Cancelado', bg: 'bg-red-500/20 text-red-300 border-red-500/40' };
    case 'in_progress':
      return { label: 'Em Atendimento', bg: 'bg-purple-500/20 text-purple-300 border-purple-500/40' };
    case 'no_show':
      return { label: 'Faltou', bg: 'bg-slate-500/20 text-slate-300 border-slate-500/40' };
    default:
      return { label: 'Pendente', bg: 'bg-amber-500/20 text-amber-300 border-amber-500/40' };
  }
};

const quickCheckIn = async app => {
  if (!app.id || app.is_blocked) return;
  try {
    if (String(app.id).startsWith('resched-')) {
      return;
    }
    if (app.is_enrollment && app.enrollment_id) {
      const slotDate = app.start_time ? app.start_time.split('T')[0] : null;
      await schedulingAPI.checkInEnrollment(app.enrollment_id, { attendance_date: slotDate });
      app.status = 'present';
      fetchAppointments();
      return;
    }
    await schedulingAPI.updateAppointmentStatus(app.id, 'completed');
    app.status = 'completed';
    fetchAppointments();
  } catch (err) {
    console.error('Erro ao confirmar presença:', err);
  }
};

watch([currentWeekStart, selectedProfessionalId, selectedServiceId], () => {
  fetchAppointments();
});

onMounted(() => {
  fetchAppointments();
});
</script>

<template>
  <div class="space-y-3 select-none">
    <!-- Filters & Navigation Bar (Flat & Seamless Modern Layout) -->
    <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-3 select-none pb-1">
      <!-- Left: Week Navigation + Period Selector -->
      <div class="flex flex-wrap items-center gap-3">
        <!-- Week Buttons -->
        <div class="flex items-center gap-1">
          <button
            type="button"
            class="h-8 w-8 rounded-lg bg-n-alpha-1 hover:bg-n-alpha-2 border border-n-weak text-n-slate-11 hover:text-n-slate-12 transition-colors cursor-pointer flex items-center justify-center shadow-xs"
            title="Semana Anterior"
            @click="prevWeek"
          >
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <button
            type="button"
            class="h-8 px-3 rounded-lg bg-n-alpha-1 hover:bg-n-alpha-2 border border-n-weak text-n-slate-12 font-medium text-xs transition-colors cursor-pointer shadow-xs"
            @click="goToToday"
          >
            Hoje
          </button>

          <button
            type="button"
            class="h-8 w-8 rounded-lg bg-n-alpha-1 hover:bg-n-alpha-2 border border-n-weak text-n-slate-11 hover:text-n-slate-12 transition-colors cursor-pointer flex items-center justify-center shadow-xs"
            title="Próxima Semana"
            @click="nextWeek"
          >
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <span class="text-xs font-medium text-n-slate-11 ml-2 font-mono">
            {{ weekDays[0]?.formattedDate }} — {{ weekDays[6]?.formattedDate }}
          </span>
        </div>

        <!-- Shift / Turno Filters (Segmented Control) -->
        <div class="flex items-center p-0.5 bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak/60 rounded-lg gap-0.5">
          <button
            v-for="p in periods"
            :key="p.id"
            type="button"
            class="px-2.5 py-1 text-xs rounded-md transition-all flex items-center gap-1.5 cursor-pointer"
            :class="
              selectedPeriod === p.id
                ? 'bg-n-solid-active text-n-slate-12 font-semibold shadow-xs'
                : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2/50'
            "
            @click="selectedPeriod = p.id"
          >
            <span>{{ p.label }}</span>
            <span
              v-if="getPeriodCount(p.id) > 0"
              class="px-1.5 py-0.2 rounded-full text-[10px] font-mono"
              :class="selectedPeriod === p.id ? 'bg-n-alpha-2 text-n-slate-12 font-bold' : 'bg-n-alpha-1 text-n-slate-10'"
            >
              {{ getPeriodCount(p.id) }}
            </span>
          </button>
        </div>
      </div>

      <!-- Right: Custom Dropdowns & Action Button -->
      <div class="flex items-center gap-2">
        <!-- Professional Filter Dropdown -->
        <SchedulingDropdown
          v-model="selectedProfessionalId"
          :options="professionalOptions"
          placeholder="Todos os Profissionais"
          align="right"
          @change="onProfessionalChange"
        >
          <template #icon>
            <svg class="w-3.5 h-3.5 text-purple-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </template>
        </SchedulingDropdown>

        <!-- Service Filter Dropdown -->
        <SchedulingDropdown
          v-model="selectedServiceId"
          :options="serviceOptions"
          placeholder="Todos os Serviços"
          align="right"
          @change="onServiceChange"
        >
          <template #icon>
            <svg class="w-3.5 h-3.5 text-emerald-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </template>
        </SchedulingDropdown>

        <!-- New Appointment Button -->
        <button
          type="button"
          class="h-8 px-3 bg-brand hover:bg-brand-hover active:bg-brand text-white font-medium text-xs rounded-lg shadow-xs transition-colors flex items-center gap-1.5 cursor-pointer shrink-0"
          @click="emit('open-appointment-modal', null)"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          <span>Novo Agendamento</span>
        </button>
      </div>
    </div>

    <!-- Weekly Grid (Ultra-Clean & Minimalist Table) -->
    <div class="overflow-x-auto rounded-xl border border-n-weak bg-n-surface-1 shadow-xs">
      <div class="min-w-[950px]">
        <!-- Days Header -->
        <div class="grid grid-cols-[72px_repeat(7,minmax(0,1fr))] border-b border-n-weak bg-n-surface-1 sticky top-0 z-20">
          <div class="py-2.5 px-2 text-[10px] font-semibold uppercase tracking-wider text-n-slate-10 text-center border-r border-n-weak/60 flex items-center justify-center">
            HORÁRIO
          </div>
          <div
            v-for="d in weekDays"
            :key="d.dateString"
            class="py-2 px-1 text-center border-r border-n-weak/60 last:border-r-0 transition-colors cursor-pointer flex items-center justify-center hover:bg-n-alpha-1/40"
            title="Clique para agendar neste dia"
            @click="handleDayHeaderClick(d)"
          >
            <div class="flex flex-col items-center justify-center select-none py-1">
              <span
                class="text-[11px] font-semibold uppercase tracking-wider leading-none"
                :class="d.isToday ? 'text-n-brand' : 'text-n-slate-11'"
              >
                {{ d.dayName }}
              </span>
              <span
                v-if="d.isToday"
                class="inline-flex items-center justify-center px-2 py-0.5 rounded-full bg-brand text-white text-xs font-semibold font-mono mt-1 shadow-xs"
              >
                {{ d.formattedDate }}
              </span>
              <span
                v-else
                class="text-xs font-medium font-mono text-n-slate-12 mt-1 leading-none"
              >
                {{ d.formattedDate }}
              </span>
            </div>
          </div>
        </div>

        <!-- Loading Spinner -->
        <div v-if="isLoading" class="flex justify-center items-center py-24 bg-n-surface-1">
          <div class="animate-spin rounded-full h-7 w-7 border-2 border-brand border-t-transparent"></div>
        </div>

        <!-- Time Slots Rows -->
        <div v-else class="divide-y divide-n-weak/60 bg-n-surface-1">
          <div
            v-for="timeSlot in timeSlots"
            :key="timeSlot"
            class="grid grid-cols-[72px_repeat(7,minmax(0,1fr))] min-h-[88px] transition-colors"
          >
            <!-- Slot Time Label (Clean, Minimalist Mono Font) -->
            <div class="p-2 text-center border-r border-n-weak/60 flex items-start justify-center pt-3 select-none bg-n-alpha-1/10">
              <span class="text-xs font-mono text-n-slate-10 font-medium">
                {{ timeSlot }}
              </span>
            </div>

            <!-- Day Columns -->
            <div
              v-for="d in weekDays"
              :key="`${d.dateString}-${timeSlot}`"
              class="p-1.5 border-r border-n-weak/60 last:border-r-0 relative group transition-colors"
              :class="[
                d.isToday ? 'bg-n-alpha-1/25' : '',
                isSlotFull(d.dateString, timeSlot) ? 'bg-n-alpha-1/10' : 'cursor-pointer hover:bg-n-alpha-1/50'
              ]"
              @click="handleSlotClick(d.dateString, timeSlot)"
            >
              <!-- Appointments Inside this Slot -->
              <div class="space-y-1.5 h-full">
                <div
                  v-for="app in getAppointmentsForSlot(d.dateString, timeSlot)"
                  :key="app.id"
                  class="p-2 rounded-lg text-xs font-sans shadow-xs border transition-all cursor-pointer relative overflow-hidden bg-n-surface-1 border-n-weak hover:border-brand/40"
                  :class="app.is_blocked ? 'bg-amber-500/5 border-amber-500/30 text-amber-600 dark:text-amber-400' : ''"
                  @click.stop="!app.is_blocked && emit('open-appointment-modal', app)"
                >
                  <!-- Color Accent Bar by Service (Subtle 2px) -->
                  <div
                    class="absolute left-0 top-0 bottom-0 w-[3px] rounded-l"
                    :style="{ backgroundColor: app.is_blocked ? '#f59e0b' : (app.service?.color || 'var(--brand-color, #2781F6)') }"
                  />

                  <div class="pl-1 space-y-1">
                    <!-- Card de Bloqueio -->
                    <div v-if="app.is_blocked" class="space-y-1">
                      <div class="flex items-center justify-between gap-1">
                        <span class="font-semibold text-amber-600 dark:text-amber-400 flex items-center gap-1 text-[11px]">
                          <span>🔒</span> Bloqueio
                        </span>
                        <span class="text-[9px] px-1.5 py-0.5 rounded font-medium bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                          Indisponível
                        </span>
                      </div>
                      <div class="text-[11px] text-n-slate-11 font-normal truncate" :title="app.notes">
                        {{ app.notes.replace('🔒 Bloqueio: ', '') }}
                      </div>
                    </div>

                    <!-- Card de Atendimento Normal -->
                    <template v-else>
                      <div class="flex items-center justify-between gap-1">
                        <span class="font-semibold text-n-slate-12 truncate max-w-[105px]" :title="app.patient?.name">
                          {{ app.patient?.name || 'Sem nome' }}
                        </span>
                        <!-- Session Type Badge (Clean Neutral) -->
                        <span
                          v-if="app.session_type === 'package'"
                          class="text-[9px] px-1.5 py-0.2 rounded font-mono font-medium bg-n-alpha-2 text-n-slate-11 border border-n-weak/60 shrink-0"
                          title="Sessão de Pacote / Plano"
                        >
                          {{ app.session_number }}/{{ app.total_sessions }}
                        </span>
                        <span
                          v-else-if="app.session_type === 'trial'"
                          class="text-[9px] px-1.5 py-0.2 rounded font-medium bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 shrink-0"
                        >
                          Exp
                        </span>
                      </div>

                      <div class="text-[11px] text-n-slate-11 flex items-center justify-between">
                        <span class="truncate font-normal">{{ app.service?.name }}</span>
                        <span class="text-[10px] text-n-slate-10 font-mono">
                          {{ parseAppDateTime(app.start_time).timeStr }}
                        </span>
                      </div>

                      <div v-if="app.professional" class="text-[10px] text-n-slate-10 font-normal truncate">
                        {{ app.professional.name }}
                      </div>

                      <!-- Rodapé de Ação: Presença / Concluído -->
                      <div class="pt-1 mt-1 border-t border-n-weak/50 flex items-center justify-between">
                        <span
                          v-if="app.status === 'completed'"
                          class="text-[10px] font-medium text-emerald-600 dark:text-emerald-400 flex items-center gap-1"
                        >
                          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
                          </svg>
                          <span>Presente</span>
                        </span>
                        <button
                          v-else
                          type="button"
                          class="w-full text-center py-0.5 px-1.5 rounded text-[10px] font-medium bg-n-alpha-1 hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12 border border-n-weak hover:border-n-slate-10 transition-colors cursor-pointer flex items-center justify-center gap-1"
                          title="Confirmar presença deste atendimento"
                          @click.stop="quickCheckIn(app)"
                        >
                          <svg class="w-2.5 h-2.5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
                          </svg>
                          <span>Confirmar Presença</span>
                        </button>
                      </div>
                    </template>
                  </div>
                </div>

                <!-- Empty Slot Hover Action -->
                <div
                  v-if="!isSlotFull(d.dateString, timeSlot) && getAppointmentsForSlot(d.dateString, timeSlot).length === 0"
                  class="h-full min-h-[44px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                >
                  <span class="text-[10px] font-medium text-n-slate-11 group-hover:text-n-brand bg-n-alpha-1 border border-n-weak px-2 py-0.5 rounded-md shadow-xs flex items-center gap-1">
                    <span>+</span>
                    <span>Agendar</span>
                  </span>
                </div>

                <!-- Vagas Livres Restantes quando já houver atendimento (Discreto) -->
                <div
                  v-else-if="!isSlotFull(d.dateString, timeSlot) && getAppointmentsForSlot(d.dateString, timeSlot).length > 0"
                  class="text-[10px] font-medium text-n-slate-10 hover:text-n-brand text-center py-0.5 cursor-pointer transition-colors"
                  title="Clique para agendar mais um paciente neste horário"
                  @click.stop="handleSlotClick(d.dateString, timeSlot)"
                >
                  +{{ getMaxCapacity(getAppointmentsForSlot(d.dateString, timeSlot)) - getAppointmentsForSlot(d.dateString, timeSlot).length }} vaga(s)
                </div>

                <!-- Slot Full Badge -->
                <div
                  v-else-if="isSlotFull(d.dateString, timeSlot)"
                  class="text-[9px] font-medium text-n-slate-10 text-center py-0.5 font-mono"
                >
                  Lotado ({{ getAppointmentsForSlot(d.dateString, timeSlot).length }}/{{ getMaxCapacity(getAppointmentsForSlot(d.dateString, timeSlot)) }})
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
